import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../apiTools/apiClient';
import {
  FaMicrophone,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaTrash,
  FaSearch,
  FaLock
} from 'react-icons/fa';

// Reuses database-audit.css deliberately — same modal/embedded shell, tabs and alert
// classes as DatabaseAudit, so this panel reads as part of the same admin surface
// instead of introducing a second look for the same job.
import '../../styles/database-audit.css';

/**
 * VoiceSlotManager — admin panel for ElevenLabs custom-voice slots.
 *
 * WHY IT EXISTS: AutoCast died mid-run on a hard provider cap
 * ("custom voices 660 / 660") with no way to see it coming. This surfaces headroom,
 * the live-vs-orphan split, and the reclaim controls.
 *
 * SAFETY SHAPE (matches the server contract in voiceSlotController):
 *   - Reclaim is always PREVIEW FIRST. The Apply button does not exist until a
 *     preview has run, so you cannot delete a selection you haven't seen.
 *   - Apply requires typing RECLAIM, and sends confirm:'RECLAIM'.
 *   - Changing scope or categories invalidates the preview, so the thing you
 *     confirm is always the thing you previewed.
 *   - Cap-relevant categories are labelled: deleting `professional` (trained PVC)
 *     destroys actor-derived work and frees NO cap slot.
 */
const CATEGORY_NOTES = {
  generated: 'Voice Design / AutoCast saves — regenerated by a re-cast. Counts against the cap.',
  cloned: 'IVC clones from real actor samples. Counts against the cap.',
  professional: 'Trained PVCs. Expensive, actor-derived — and NOT counted against the cap, so deleting one frees nothing.'
};

const VoiceSlotManager = ({ onClose, embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [audit, setAudit] = useState(null);
  const [includeRenderHistory, setIncludeRenderHistory] = useState(false);

  // Reclaim form
  const [includeReferenced, setIncludeReferenced] = useState(false);
  const [categories, setCategories] = useState(['generated']);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const loadAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/voice-slots/audit', {}, {
        includeRenderHistory: includeRenderHistory ? 1 : 0
      });
      setAudit(res.data || res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load voice-slot audit');
    } finally {
      setLoading(false);
    }
  }, [includeRenderHistory]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  // Any scope change invalidates a previously-run preview — otherwise the operator
  // could preview a narrow selection, widen the scope, and confirm the wider one.
  const changeScope = (fn) => { setPreview(null); setConfirmText(''); fn(); };

  const toggleCategory = (cat) => changeScope(() => {
    setCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  });

  const runReclaim = async (apply) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const body = { apply, includeReferenced, categories, concurrency: 4 };
      if (apply) body.confirm = 'RECLAIM';
      const res = await apiClient.post('/voice-slots/reclaim', body);
      const m = res.data || res;
      if (apply) {
        setSuccess(`Deleted ${m.deleted} of ${m.totals.selected} voice(s).`
          + (m.failures?.length ? ` ${m.failures.length} failed — re-running is safe.` : '')
          + (m.totals.selectedReferenced
            ? ` ${m.totals.selectedReferenced} were referenced — run voiceCloneGraphCleanup.js to clear the dead rows.`
            : ''));
        setPreview(null);
        setConfirmText('');
        loadAudit();
      } else {
        setPreview(m);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Reclaim request failed');
    } finally {
      setBusy(false);
    }
  };

  const capPct = audit ? Math.min(100, Math.round((audit.cap.used / audit.cap.limit) * 100)) : 0;
  const canApply = preview && preview.totals.selected > 0 && confirmText === 'RECLAIM';

  return (
    <div className={embedded ? 'database-audit-embedded' : 'database-audit-modal'}>
      <div className="database-audit-container">
        <div className="audit-header">
          <h2><FaMicrophone /> Voice Slots (ElevenLabs)</h2>
          {!embedded && <button className="close-button" onClick={onClose}>×</button>}
        </div>

        {error && <div className="alert alert-danger"><FaTimesCircle /> {error}</div>}
        {success && <div className="alert alert-success"><FaCheckCircle /> {success}</div>}

        {loading && <p>Loading voice-slot audit…</p>}

        {audit && (
          <>
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 6 }}>
                Custom-voice cap: {audit.cap.used} / {audit.cap.limit}
                <span style={{ fontWeight: 400, fontSize: '0.8em', marginLeft: 10 }}>
                  {audit.cap.free} free
                </span>
              </h3>
              <div style={{ background: '#e9ecef', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                <div style={{
                  width: `${capPct}%`,
                  height: '100%',
                  background: capPct > 90 ? '#dc3545' : capPct > 70 ? '#ffc107' : '#28a745'
                }} />
              </div>
              <small>
                The cap counts <code>{audit.cap.countsCategories.join('</code> + <code>')}</code> only.
                {' '}Listed via <code>{audit.endpoint}</code>{audit.mock ? ' (MOCK client)' : ''}.
              </small>
            </div>

            <table className="table table-sm" style={{ maxWidth: 620 }}>
              <thead>
                <tr>
                  <th>Category</th><th>Total</th><th>Referenced</th><th>Orphaned</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(audit.byCategory).sort().map((cat) => (
                  <tr key={cat}>
                    <td><code>{cat}</code></td>
                    <td>{audit.byCategory[cat]}</td>
                    <td>{audit.referencedByCategory[cat] || 0}</td>
                    <td>{audit.orphansByCategory[cat] || 0}</td>
                    <td style={{ fontSize: '0.82em' }}>
                      {CATEGORY_NOTES[cat] || (cat === 'premade' ? 'Shared library — not ours, never a candidate.' : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ fontSize: '0.88em' }}>
              <strong>{audit.totals.custom}</strong> custom · <strong>{audit.totals.referenced}</strong> referenced
              {' · '}<strong>{audit.totals.orphans}</strong> orphaned
              {audit.totals.dangling > 0 && (
                <span style={{ color: '#dc3545' }}>
                  {' · '}<FaExclamationTriangle /> {audit.totals.dangling} graph row(s) point at a voice
                  that no longer exists — these already fail to render.
                </span>
              )}
            </p>

            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={includeRenderHistory}
                onChange={(e) => setIncludeRenderHistory(e.target.checked)}
              />{' '}
              Also check render history (slow — full <code>RenderedGridCell</code> scan on an
              unindexed property; can hang while an import is running)
              {audit.renderHistoryChecked && audit.totals.orphansWithRenderHistory !== null && (
                <> — <strong>{audit.totals.orphansWithRenderHistory}</strong> orphan(s) produced existing audio</>
              )}
            </label>

            <button className="btn btn-secondary btn-sm" onClick={loadAudit} disabled={loading || busy}>
              <FaSync /> Refresh
            </button>

            <hr />

            <h3><FaTrash /> Reclaim</h3>

            <div style={{ marginBottom: 10 }}>
              <label style={{ marginRight: 20 }}>
                <input
                  type="radio"
                  checked={!includeReferenced}
                  onChange={() => changeScope(() => setIncludeReferenced(false))}
                />{' '}
                Orphans only <small>(nothing references them — safe)</small>
              </label>
              <label>
                <input
                  type="radio"
                  checked={includeReferenced}
                  onChange={() => changeScope(() => setIncludeReferenced(true))}
                />{' '}
                <strong style={{ color: '#dc3545' }}>All custom voices</strong>{' '}
                <small>(includes live ones — requires a re-cast afterwards)</small>
              </label>
            </div>

            <div style={{ marginBottom: 10 }}>
              <small>Categories (none selected = every custom category):</small><br />
              {['generated', 'cloned', 'professional'].map((cat) => (
                <label key={cat} style={{ marginRight: 16, fontSize: '0.9em' }}>
                  <input
                    type="checkbox"
                    checked={categories.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                  />{' '}
                  <code>{cat}</code>
                </label>
              ))}
            </div>

            {includeReferenced && (
              <div className="alert alert-warning" style={{ fontSize: '0.86em' }}>
                <FaExclamationTriangle /> This scope deletes voices the graph still uses. Affected
                characters lose their voice until re-cast, and you must then run
                {' '}<code>voiceCloneGraphCleanup.js</code> — otherwise AutoCast <em>skips</em> them
                (it ignores any character that still has a <code>VOICED_BY</code> edge) and they
                silently never get a working voice.
              </div>
            )}
            {categories.includes('professional') && (
              <div className="alert alert-warning" style={{ fontSize: '0.86em' }}>
                <FaExclamationTriangle /> <code>professional</code> PVCs are trained, actor-derived,
                and sit outside the cap — deleting them destroys expensive work and frees no slot.
              </div>
            )}

            <button
              className="btn btn-primary btn-sm"
              onClick={() => runReclaim(false)}
              disabled={busy}
              style={{ marginRight: 10 }}
            >
              <FaSearch /> {busy ? 'Working…' : 'Preview selection'}
            </button>

            {preview && (
              <div style={{ marginTop: 14, padding: 12, border: '1px solid #ccc', borderRadius: 4 }}>
                <p style={{ marginBottom: 6 }}>
                  <strong>{preview.totals.selected}</strong> voice(s) would be deleted
                  {' — '}{preview.totals.selectedReferenced} referenced, {preview.totals.selectedOrphaned} orphaned.
                  {preview.totals.preserved > 0 && (
                    <> <strong>{preview.totals.preserved}</strong> preserved
                    {' '}({Object.entries(preview.preservedByCategory).map(([c, n]) => `${n} ${c}`).join(', ')}).</>
                  )}
                </p>
                {preview.totals.selected === 0 ? (
                  <p><FaCheckCircle /> Nothing matches this scope.</p>
                ) : (
                  <>
                    <ul style={{ fontSize: '0.82em', maxHeight: 160, overflowY: 'auto' }}>
                      {preview.targets.map((t) => (
                        <li key={t.voiceId}>
                          <code>{t.voiceId}</code> [{t.category}] {t.name}
                          {t.wasReferenced && <strong style={{ color: '#dc3545' }}> · live</strong>}
                        </li>
                      ))}
                      {preview.targetsTruncated && <li>…list truncated for display</li>}
                    </ul>
                    <label style={{ fontSize: '0.9em' }}>
                      <FaLock /> Type <code>RECLAIM</code> to enable deletion:{' '}
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        style={{ width: 120 }}
                      />
                    </label>
                    <br />
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => runReclaim(true)}
                      disabled={!canApply || busy}
                    >
                      <FaTrash /> {busy ? 'Deleting…' : `Delete ${preview.totals.selected} voice(s)`}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceSlotManager;
